"use client";
import { Editor, EditorContent } from '@tiptap/react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Code2,
  Droplet,
  FileText,
  Heading1,
  Heading2,
  Heading3,
  ImageIcon,
  Italic,
  LinkIcon,
  List,
  ListOrdered,
  Minus,
  Palette,
  Quote,
  Redo,
  RemoveFormatting,
  Strikethrough,
  Subscript,
  Superscript,
  Table as TableIcon,
  Trash2,
  Underline as UnderlineIcon,
  Undo,
  Upload,
  Video,
  X
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import LinkModal from './LinkModal';
import { ToastContainer, useToast } from '@/components/ui/ToastContainer';
import { compressImageIfNeeded, compressImage } from '@/lib/utils/imageCompression';

interface ContentEditorProps {
  editor: Editor | null;
  editorReady: boolean;
  fieldErrors: Record<string, string>;
  handleVideoUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
}

export default function ContentEditor({
  editor,
  editorReady,
  fieldErrors,
  handleVideoUpload
}: ContentEditorProps) {
  const { showToast, removeToast, toasts } = useToast();

  // Predefined Colors Dropdown Component
  const PredefinedColorsDropdown = ({ editor }: { editor: Editor | null }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    const predefinedColors = [
      { name: 'Paragraph', color: '#374151', description: 'Standard text color' },
      { name: 'Heading', color: '#1b1b1b', description: 'Heading text color' },
      { name: 'Danger', color: '#dc2626', description: 'Error or warning text' },
      { name: 'Link', color: '#2563eb', description: 'Link text color' },
    ];

    const currentColor = editor?.getAttributes('textStyle').color || '#000000';

    const applyColor = (color: string) => {
      if (editor && color) {
        try {
          editor.chain().focus().setColor(color).run();
        } catch {
          editor.chain().focus().setMark('textStyle', { color }).run();
        }
        setShowDropdown(false);
      }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setShowDropdown(false);
        }
      };

      if (showDropdown) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
      return undefined;
    }, [showDropdown]);

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className={`px-3 py-2 rounded transition-all flex items-center gap-2 text-sm font-medium ${
            showDropdown 
              ? 'bg-sky-100 border-2 border-sky-300 text-sky-700' 
              : 'hover:bg-sky-50 border-2 border-sky-200 text-gray-700'
          }`}
          title="Pre-defined Colors - Quick color selection"
        >
          <Palette className={`h-4 w-4 ${showDropdown ? 'text-sky-600' : 'text-gray-600'}`} />
          <span>Pre-defined Colors</span>
        </button>

        {showDropdown && (
          <div className="absolute left-0 top-full mt-2 z-50 bg-white border-2 border-sky-200 rounded-xl shadow-2xl min-w-[280px] overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-sky-50 via-slate-50 to-sky-100 border-b border-sky-200">
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-sky-600" />
                <p className="text-sm font-semibold text-gray-800">Quick Colors</p>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Select a predefined color</p>
            </div>

            {/* Color Options */}
            <div className="p-3 space-y-1 bg-gradient-to-b from-sky-50/40 via-slate-50/60 to-sky-100/40">
              {predefinedColors.map((item) => {
                const isActive = item.color.toLowerCase() === currentColor.toLowerCase();
                return (
                  <button
                    key={item.name}
                    onClick={() => applyColor(item.color)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-sky-100 border-2 border-sky-300 shadow-sm'
                        : 'hover:bg-sky-50/80 border-2 border-transparent'
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg border-2 flex-shrink-0 shadow-sm ${
                        isActive ? 'border-sky-400 ring-2 ring-sky-200' : 'border-sky-200'
                      }`}
                      style={{ backgroundColor: item.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold text-sm ${isActive ? 'text-sky-700' : 'text-gray-800'}`}>
                          {item.name}
                        </span>
                        {isActive && (
                          <span className="text-xs px-1.5 py-0.5 bg-sky-200 text-sky-700 rounded font-medium">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                    </div>
                    <span className="text-xs text-gray-400 font-mono bg-sky-50 px-2 py-1 rounded border border-sky-100">
                      {item.color}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Custom Color Picker Component
  const CustomColorPicker = ({ editor }: { editor: Editor | null }) => {
    const [showPicker, setShowPicker] = useState(false);
    const [customColor, setCustomColor] = useState('#000000');
    const [hexInput, setHexInput] = useState('#000000');
    const pickerRef = useRef<HTMLDivElement | null>(null);

    const currentColor = editor?.getAttributes('textStyle').color || '#000000';

    const applyColor = (color: string) => {
      if (!editor || !color) return;
      
      // Ensure color is in proper format
      const normalizedColor = color.startsWith('#') ? color : `#${color}`;
      
      // Try multiple methods to apply color
      try {
        // First try the setColor method
        if (editor.chain().focus().setColor(normalizedColor).run()) {
          return;
        }
      } catch {
        // If that fails, try setMark
      }
      
      try {
        // Try setMark with textStyle
        editor.chain().focus().setMark('textStyle', { color: normalizedColor }).run();
      } catch {
        // If that fails, try extendMarkRange first
        try {
          editor.chain().focus().extendMarkRange('textStyle').setMark('textStyle', { color: normalizedColor }).run();
        } catch (e2) {
          console.error('Error applying color:', e2);
        }
      }
    };

    const handleHexInputChange = (value: string) => {
      // Remove # if present
      let hex = value.replace('#', '');
      // Only allow hex characters
      hex = hex.replace(/[^0-9A-Fa-f]/g, '');
      // Limit to 6 characters
      hex = hex.substring(0, 6);
      
      const formattedHex = hex ? `#${hex}` : '#';
      setHexInput(formattedHex);
      setCustomColor(formattedHex);
      
      // Update preview but don't auto-apply - user must click Apply button
    };

    const handleColorPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const color = e.target.value.toUpperCase();
      setCustomColor(color);
      setHexInput(color);
      // Only update preview - don't auto-apply, user must click Apply button
    };

    // Close picker when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
          setShowPicker(false);
        }
      };

      if (showPicker) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
      return undefined;
    }, [showPicker]);

    // Initialize with current color when opening
    useEffect(() => {
      if (showPicker) {
        setHexInput(currentColor);
        setCustomColor(currentColor);
      }
    }, [showPicker, currentColor]);

    return (
      <div className="relative" ref={pickerRef}>
        <button
          onClick={() => setShowPicker(!showPicker)}
          className={`p-2 rounded transition-all flex items-center gap-1.5 ${
            showPicker 
              ? 'bg-sky-100 border-2 border-sky-300' 
              : 'hover:bg-sky-50 border-2 border-sky-200'
          }`}
          title="Custom Color - Choose any color"
        >
          <Droplet className={`h-4 w-4 ${showPicker ? 'text-sky-600' : 'text-gray-600'}`} />
        </button>

        {showPicker && (
          <div className="absolute left-0 top-full mt-2 z-50 bg-white border-2 border-sky-200 rounded-xl shadow-2xl min-w-[300px] overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-sky-50 via-slate-50 to-sky-100 border-b border-sky-200">
              <div className="flex items-center gap-2">
                <Droplet className="h-4 w-4 text-sky-600" />
                <p className="text-sm font-semibold text-gray-800">Custom Color</p>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Pick any color using dropper or hex code</p>
            </div>

            {/* Color Picker Section */}
            <div className="p-4 space-y-4 bg-gradient-to-b from-sky-50/40 via-slate-50/60 to-sky-100/40">
              {/* Color Dropper */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Color Dropper
                </label>
                <div className="flex items-center gap-3">
                  <div className="relative group">
                    <input
                      type="color"
                      value={customColor}
                      onChange={handleColorPickerChange}
                      className="w-16 h-16 border-3 border-sky-200 rounded-lg cursor-pointer shadow-md hover:shadow-lg transition-shadow"
                      title="Click to open color picker (dropper)"
                    />
                  </div>
                  <div className="flex-1">
                    <div
                      className="w-full h-12 rounded-lg border-2 border-sky-200 shadow-sm mb-2"
                      style={{ backgroundColor: customColor }}
                    />
                    <p className="text-xs text-gray-500">Preview</p>
                  </div>
                </div>
              </div>

              {/* Hex Input */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Hex Color Code
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-mono text-sm">#</span>
                  <input
                    type="text"
                    value={hexInput.replace('#', '')}
                    onChange={(e) => handleHexInputChange(e.target.value)}
                    placeholder="000000"
                    className="w-full pl-8 pr-3 py-2.5 text-sm border-2 border-sky-200 rounded-lg focus:outline-none focus:border-sky-500 font-mono bg-white"
                    maxLength={6}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  Enter 6-digit hex code, then click Apply
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="px-4 py-3 bg-gradient-to-r from-sky-50 to-sky-100 border-t border-sky-200 flex items-center gap-2">
              <button
                onClick={() => {
                  if (customColor && customColor.length >= 6) {
                    applyColor(customColor);
                    setShowPicker(false);
                  }
                }}
                disabled={!customColor || customColor.length < 6}
                className="flex-1 px-4 py-2 text-sm bg-gradient-to-r from-sky-500 via-sky-600 to-blue-600 text-white rounded-lg hover:from-sky-600 hover:via-sky-700 hover:to-blue-700 transition-all font-semibold shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply Color
              </button>
              <button
                onClick={() => setShowPicker(false)}
                className="px-3 py-2 text-sm text-gray-600 hover:bg-sky-100 rounded-lg transition-colors border border-sky-200"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Link modal state
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [initialLinkUrl, setInitialLinkUrl] = useState<string>('');
  const [initialLinkRel, setInitialLinkRel] = useState<string | null>(null);
  // Save selection when opening modal so we can apply link to it after focus is lost (avoids overwriting other links in same paragraph)
  const [linkModalSelection, setLinkModalSelection] = useState<{ from: number; to: number } | null>(null);
  const [isEditingExistingLink, setIsEditingExistingLink] = useState(false);

    const openLinkModal = () => {
      if (!editor || !editorReady) {
        alert('Editor is not ready yet. Please wait a moment.');
        return;
      }
      const attrs = editor.getAttributes('link');
      setInitialLinkUrl((attrs?.href as string) || '');
      setInitialLinkRel((attrs?.rel as string) ?? null);
      const editingLink = editor.isActive('link');
      setIsEditingExistingLink(editingLink);
      // When adding a new link (not editing), save current selection so we don't expand over adjacent links
      if (!editingLink) {
        const { from, to } = editor.state.selection;
        setLinkModalSelection({ from, to });
      } else {
        setLinkModalSelection(null);
      }
      setIsLinkModalOpen(true);
    };

    const onConfirmLink = ({ url, rel, openInNewTab }: { url: string; rel: string | null; openInNewTab: boolean }) => {
      try {
        const attrs: { href: string; target?: string | null; rel?: string | null } = { href: url };
        if (rel !== null) attrs.rel = rel;
        if (openInNewTab) attrs.target = '_blank';
        if (!editor) return;
        const chain = editor.chain().focus();
        // Editing existing link: extend to full link range then set (keeps single link update)
        if (isEditingExistingLink) {
          chain.extendMarkRange('link').setLink(attrs).run();
        } else {
          // New link: apply only to the saved selection so other links in the same paragraph are preserved
          if (linkModalSelection) {
            chain.setTextSelection(linkModalSelection).setLink(attrs).run();
            setLinkModalSelection(null);
          } else {
            chain.setLink(attrs).run();
          }
        }
      } catch (e) {
        console.error('Error inserting link:', e);
        alert('Failed to insert link');
      } finally {
        setIsLinkModalOpen(false);
      }
    };

    const addImageFromUrl = () => {
      if (!editor || !editorReady) {
        alert('Editor is not ready yet. Please wait a moment.');
        return;
      }

      const url = window.prompt('Image URL');
      if (url) {
        try {
          editor.chain().focus().setImage({ src: url }).run();
        } catch (error) {
          console.error('Error adding image from URL:', error);
          alert('Failed to add image from URL');
        }
      }
    };

    const addImageFromFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!editor || !editorReady) {
        showToast('Editor is not ready yet. Please wait a moment.', 'error');
        // Clear the input
        if (e.target) {
          e.target.value = '';
        }
        return;
      }

      const file = e.target.files?.[0];
      if (!file) {
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showToast('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.', 'error');
        // Clear the input
        if (e.target) {
          e.target.value = '';
        }
        return;
      }

      try {
        // Compress image if needed (will compress to below 1MB)
        let compressedFile = await compressImageIfNeeded(file, showToast);
        
        // Verify compression worked
        const maxSizeBytes = 1 * 1024 * 1024; // 1MB
        if (compressedFile.size > maxSizeBytes) {
          console.warn(`Compressed file is still ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB, attempting more aggressive compression...`);
          compressedFile = await compressImage(compressedFile, 0.95, 0.5);
          if (compressedFile.size > maxSizeBytes) {
            throw new Error(`Unable to compress image below 1MB. Final size: ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB`);
          }
        }

        const formDataUpload = new FormData();
        formDataUpload.append('file', compressedFile);

        const response = await fetch('/api/upload/blogs', {
          method: 'POST',
          body: formDataUpload,
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Upload error response:', errorData);
          throw new Error(errorData.error || 'Failed to upload image');
        }

        const result = await response.json();
        console.warn('Upload success:', result);

        // Insert image into editor
        if (editor && result.fileUrl) {
          console.warn('Inserting image into editor:', result.fileUrl);
          editor.chain().focus().setImage({ src: result.fileUrl }).run();
        }

        // Clear the input
        if (e.target) {
          e.target.value = '';
        }

        showToast('Image uploaded successfully!', 'success');
      } catch (err) {
        console.error('Upload error:', err);
        showToast(err instanceof Error ? err.message : 'Failed to upload image', 'error');
        // Clear the input on error
        if (e.target) {
          e.target.value = '';
        }
      }
    };

    const addLink = () => {
      openLinkModal();
    };

    // Link menu state and helpers
    const [showLinkMenu, setShowLinkMenu] = useState(false);
    const [isLinkActive, setIsLinkActive] = useState(false);
    const [savedSelection, setSavedSelection] = useState<{ from: number; to: number } | null>(null);

    useEffect(() => {
      if (!editor) return;
      const update = () => {
        const active = editor.isActive('link');
        setIsLinkActive(active);
        if (active) {
          const { from, to } = editor.state.selection;
          setSavedSelection({ from, to });
          setShowLinkMenu(true);
        } else {
          setShowLinkMenu(false);
        }
      };
      update();
      editor.on('selectionUpdate', update);
      editor.on('transaction', update);
      return () => {
        editor.off('selectionUpdate', update);
        editor.off('transaction', update);
      };
    }, [editor]);

    const setRel = (rel: string | null) => {
      if (!editor || !editorReady) {
        alert('Editor is not ready yet. Please wait a moment.');
        return;
      }
      if (savedSelection) {
        editor.commands.setTextSelection(savedSelection);
      }
      const attrs = editor.getAttributes('link');
      const href = attrs?.href;
      if (!href) {
        alert('Select a link or add one first (Ctrl+K).');
        return;
      }
      try {
        editor.chain().focus().extendMarkRange('link').setLink({ href, rel }).run();
        setShowLinkMenu(false);
      } catch (error) {
        console.error('Error setting rel:', error);
        alert('Failed to update link attribute');
      }
    };

    const unlinkSelection = () => {
      if (!editor) return;
      try {
        const selectionToUse = savedSelection || { from: editor.state.selection.from, to: editor.state.selection.to };
        editor.chain().focus().setTextSelection(selectionToUse).extendMarkRange('link').unsetLink().run();
        // If for any reason link mark still active (edge cases), try once more on current selection
        if (editor.isActive('link')) {
          editor.chain().focus().unsetLink().run();
        }
        setShowLinkMenu(false);
      } catch (error) {
        console.error('Error unlinking:', error);
        alert('Failed to remove link');
      }
    };

    const addTable = () => {
      if (!editor || !editorReady) {
        alert('Editor is not ready yet. Please wait a moment.');
        return;
      }

      try {
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
      } catch (error) {
        console.error('Error adding table:', error);
        alert('Failed to add table');
      }
    };

    if (!editor) return null;

  const renderToolbar = () => (
      <div className="tiptap-toolbar">
        {/* Undo/Redo */}
        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo (Ctrl+Z)"
          >
            <Undo className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo (Ctrl+Y)"
          >
            <Redo className="h-4 w-4" />
          </button>
        </div>

        <div className="toolbar-divider"></div>

        {/* Text Formatting */}
        <div className="toolbar-group">
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().setParagraph().run();
              }
            }}
            className={editor.isActive('paragraph') ? 'active' : ''}
            title="Paragraph"
          >
            <FileText className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().toggleBold().run();
              }
            }}
            className={editor.isActive('bold') ? 'active' : ''}
            title="Bold (Ctrl+B)"
          >
            <Bold className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().toggleItalic().run();
              }
            }}
            className={editor.isActive('italic') ? 'active' : ''}
            title="Italic (Ctrl+I)"
          >
            <Italic className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().toggleUnderline().run();
              }
            }}
            className={editor.isActive('underline') ? 'active' : ''}
            title="Underline (Ctrl+U)"
          >
            <UnderlineIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().toggleStrike().run();
              }
            }}
            className={editor.isActive('strike') ? 'active' : ''}
            title="Strikethrough"
          >
            <Strikethrough className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().toggleCode().run();
              }
            }}
            className={editor.isActive('code') ? 'active' : ''}
            title="Inline Code (Ctrl+`)"
          >
            <Code className="h-4 w-4" />
          </button>
        </div>

        <div className="toolbar-divider"></div>

        {/* Headings */}
        <div className="toolbar-group">
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().setHeading({ level: 1 }).run();
              }
            }}
            className={editor.isActive('heading', { level: 1 }) ? 'active' : ''}
            title="Heading 1"
          >
            <Heading1 className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().setHeading({ level: 2 }).run();
              }
            }}
            className={editor.isActive('heading', { level: 2 }) ? 'active' : ''}
            title="Heading 2"
          >
            <Heading2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().setHeading({ level: 3 }).run();
              }
            }}
            className={editor.isActive('heading', { level: 3 }) ? 'active' : ''}
            title="Heading 3"
          >
            <Heading3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().setHeading({ level: 4 }).run();
              }
            }}
            className={editor.isActive('heading', { level: 4 }) ? 'active' : ''}
            title="Heading 4"
          >
            H4
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().setHeading({ level: 5 }).run();
              }
            }}
            className={editor.isActive('heading', { level: 5 }) ? 'active' : ''}
            title="Heading 5"
          >
            H5
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().setHeading({ level: 6 }).run();
              }
            }}
            className={editor.isActive('heading', { level: 6 }) ? 'active' : ''}
            title="Heading 6"
          >
            H6
          </button>
        </div>

        <div className="toolbar-divider"></div>

        {/* Lists */}
        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'active' : ''}
            title="Bullet List"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'active' : ''}
            title="Numbered List"
          >
            <ListOrdered className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={editor.isActive('blockquote') ? 'active' : ''}
            title="Quote"
          >
            <Quote className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={editor.isActive('codeBlock') ? 'active' : ''}
            title="Code Block"
          >
            <Code2 className="h-4 w-4" />
          </button>
        </div>

        <div className="toolbar-divider"></div>

        {/* Text Alignment */}
        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={editor.isActive({ textAlign: 'left' }) ? 'active' : ''}
            title="Align Left"
          >
            <AlignLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={editor.isActive({ textAlign: 'center' }) ? 'active' : ''}
            title="Align Center"
          >
            <AlignCenter className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={editor.isActive({ textAlign: 'right' }) ? 'active' : ''}
            title="Align Right"
          >
            <AlignRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            className={editor.isActive({ textAlign: 'justify' }) ? 'active' : ''}
            title="Justify"
          >
            <AlignJustify className="h-4 w-4" />
          </button>
        </div>

        <div className="toolbar-divider"></div>

        {/* Text Color */}
        <div className="toolbar-group">
          {/* Predefined Colors Dropdown */}
          <PredefinedColorsDropdown editor={editor} />
          {/* Custom Color Picker */}
          <CustomColorPicker editor={editor} />
        </div>

        <div className="toolbar-divider"></div>

        {/* Media & Links */}
        <div className="toolbar-group">
          <button
            onClick={addLink}
            className={editor.isActive('link') ? 'active' : ''}
            title="Add/Edit Link (Ctrl+K)"
          >
            <LinkIcon className="h-4 w-4" />
          </button>
          <div className="relative inline-block">
            <button
              onClick={() => {
                if (!isLinkActive) return;
                const { from, to } = editor.state.selection;
                setSavedSelection({ from, to });
                setShowLinkMenu((v) => !v);
              }}
              title="Link options: dofollow, nofollow, unlink"
              disabled={!isLinkActive}
              className={`ml-2 px-4 py-2 text-sm font-medium rounded-md shadow-sm transition-all duration-200 ${isLinkActive
                ? 'bg-[#7C3AED] text-white hover:bg-[#6D28D9]'
                : 'bg-white text-gray-400 border border-gray-200 cursor-not-allowed'
                }`}
            >
              Link Control
            </button>
            {showLinkMenu && (
              <div className="absolute left-0 z-20 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <p className="text-xs text-gray-500 font-medium mb-1">Current Link</p>
                  <p className="text-sm text-gray-800 truncate">
                    {(() => {
                      const attrs = editor.getAttributes('link');
                      const href = (attrs?.href || '') as string;
                      return href || 'No link selected';
                    })()}
                  </p>
                </div>
                <div className="flex flex-col divide-y divide-gray-200">
                  {(() => {
                    const attrs = editor.getAttributes('link');
                    const isNofollow = (attrs?.rel || null) === 'nofollow';
                    const isDofollow = !isNofollow; // null or undefined means dofollow
                    return (
                      <>
                        <button
                          onMouseDown={(e) => e.preventDefault()}
                          className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${isDofollow ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-purple-50 hover:text-[#7C3AED]'}`}
                          onClick={() => setRel(null)}
                        >
                          Apply <span className="font-semibold">dofollow</span>
                          {isDofollow && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-300">active</span>}
                        </button>
                        <button
                          onMouseDown={(e) => e.preventDefault()}
                          className={`w-full text-left px-4 py-2.5 border-t border-gray-200 text-sm font-medium transition-colors ${isNofollow ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-purple-50 hover:text-[#7C3AED]'}`}
                          onClick={() => setRel('nofollow')}
                        >
                          Apply <span className="font-semibold">nofollow</span>
                          {isNofollow && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-300">active</span>}
                        </button>
                        <button
                          onMouseDown={(e) => e.preventDefault()}
                          className="w-full text-left px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                          onClick={unlinkSelection}
                        >
                          Unlink
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          <button
            onClick={addImageFromUrl}
            title="Add Image from URL"
          >
            <ImageIcon className="h-4 w-4" />
          </button>
          <label className="relative cursor-pointer">
            <input
              type="file"
              accept="image/*"
              onChange={addImageFromFile}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              title="Upload Image"
              key="image-upload"
            />
            <button
              type="button"
              className="p-2 rounded hover:bg-gray-200 transition-colors text-gray-600 flex items-center justify-center"
              title="Upload Image"
            >
              <Upload className="h-4 w-4" />
            </button>
          </label>
          <label className="relative cursor-pointer">
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              title="Upload Video"
              key="video-upload"
            />
            <button
              type="button"
              className="p-2 rounded hover:bg-gray-200 transition-colors text-gray-600 flex items-center justify-center"
              title="Upload Video"
            >
              <Video className="h-4 w-4" />
            </button>
          </label>
          <button
            onClick={addTable}
            title="Add Table (3x3)"
          >
            <TableIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().setHorizontalRule().run();
              }
            }}
            title="Horizontal Rule"
          >
            <Minus className="h-4 w-4" />
          </button>
        </div>

        {/* Link Modal */}
        <LinkModal
          isOpen={isLinkModalOpen}
          initialUrl={initialLinkUrl}
          initialRel={initialLinkRel}
          onCancel={() => setIsLinkModalOpen(false)}
          onConfirm={onConfirmLink}
        />

        <div className="toolbar-divider"></div>

        {/* Table Operations */}
        {editor.isActive('table') && (
          <>
            <div className="toolbar-group">
              <button
                onClick={() => editor.chain().focus().addRowBefore().run()}
                title="Add Row Before"
                className="text-xs px-2"
              >
                Row↑
              </button>
              <button
                onClick={() => editor.chain().focus().addRowAfter().run()}
                title="Add Row After"
                className="text-xs px-2"
              >
                Row↓
              </button>
              <button
                onClick={() => editor.chain().focus().deleteRow().run()}
                title="Delete Row"
                className="text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="toolbar-divider"></div>

            <div className="toolbar-group">
              <button
                onClick={() => editor.chain().focus().addColumnBefore().run()}
                title="Add Column Before"
                className="text-xs px-2"
              >
                Col←
              </button>
              <button
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                title="Add Column After"
                className="text-xs px-2"
              >
                Col→
              </button>
              <button
                onClick={() => editor.chain().focus().deleteColumn().run()}
                title="Delete Column"
                className="text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="toolbar-divider"></div>

            <div className="toolbar-group">
              <button
                onClick={() => editor.chain().focus().mergeCells().run()}
                title="Merge Cells"
                className="text-xs px-2"
              >
                Merge
              </button>
              <button
                onClick={() => editor.chain().focus().splitCell().run()}
                title="Split Cell"
                className="text-xs px-2"
              >
                Split
              </button>
              <button
                onClick={() => editor.chain().focus().deleteTable().run()}
                title="Delete Table"
                className="text-red-600"
              >
                Del Table
              </button>
            </div>

            <div className="toolbar-divider"></div>
          </>
        )}

        {/* Script & Formatting */}
        <div className="toolbar-group">
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().toggleSubscript().run();
              }
            }}
            className={editor.isActive('subscript') ? 'active' : ''}
            title="Subscript (Ctrl+,)"
          >
            <Subscript className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().toggleSuperscript().run();
              }
            }}
            className={editor.isActive('superscript') ? 'active' : ''}
            title="Superscript (Ctrl+.)"
          >
            <Superscript className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (editor) {
                editor.chain().focus().clearNodes().unsetAllMarks().run();
              }
            }}
            title="Clear Formatting (Ctrl+\\)"
          >
            <RemoveFormatting className="h-4 w-4" />
          </button>
        </div>

      </div>
  );
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Content *
        <span className="ml-2 text-xs text-gray-500">
          ({editor?.getText().length || 0} characters)
        </span>
      </label>
      <div className={`border-2 rounded-xl overflow-hidden shadow-sm ${fieldErrors.content ? 'border-red-300' : 'border-sky-200'
        }`}>
        {renderToolbar()}
        <div className="h-[500px] relative overflow-y-auto custom-scrollbar bg-white">
          {!editorReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-sky-50/50 bg-opacity-75 z-10 backdrop-blur-sm">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600 border-t-transparent mx-auto mb-2"></div>
                <p className="text-gray-700 text-sm font-medium">Loading editor...</p>
              </div>
            </div>
          )}
          <div className="p-4">
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        {fieldErrors.content && (
          <p className="text-sm font-medium text-red-600 flex items-center gap-1">
            <span>⚠️</span> {fieldErrors.content}
          </p>
        )}
        <p className="text-xs text-gray-600 ml-auto font-medium">
          💡 Use the toolbar above to format text, add images, links, and tables
        </p>
      </div>
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
